import { createBrowserRouter } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ShellLayout } from '@/app/shell/ShellLayout';
import { BareLayout } from '@/app/shell/BareLayout';
import { HydrationRoot } from '@/app/HydrationRoot';
import { NotFound } from '@/features/placeholder/NotFound';
import { ComponentsGallery } from '@/features/dev/ComponentsGallery';
import { PersonalizationPanel } from '@/features/dev/PersonalizationPanel';
import { HomePage, HomeOrLanding } from '@/features/home';
import { PostDetailPage } from '@/features/post-detail';
import { ProfilePage, ProfileByHandlePage, EditProfilePage } from '@/features/profile';
import {
  SignInPage,
  SignUpPage,
  VerifyEmailPage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from '@/features/auth';
import { SettingsPage } from '@/features/settings';
import { NotificationsPage } from '@/features/notifications';
import { BookmarksPage } from '@/features/bookmarks';
import { PremiumPage } from '@/features/premium';
import { ExplorePage } from '@/features/explore';
import {
  VideosPage,
  VideoDetailPage,
  FlashPlayerPage,
  ShortsRedirect,
  VideoTwoFactorGate,
} from '@/features/videos';
import { LivePage, LiveDetailPage } from '@/features/live';
import { MessagesPage } from '@/features/messages';
import { CommunitiesPage, CommunityDetailPage } from '@/features/communities';
import { FeedbackPage, FeedbackDetailPage, FeedbackRoadmapPage } from '@/features/feedback';
import { SupportPage } from '@/features/support';
import { CouncilPage } from '@/features/council';
import { MorePage } from '@/features/more';
import { CreatorsHubPage } from '@/features/creators-hub';
import { OwnerPage } from '@/features/owner';
import { PoliciesIndexPage, PolicyDocumentPage } from '@/features/policies';
import { LegalFloorReportPage } from '@/features/legal-floor';
import { HelpReportingPage, HelpSafetyPage } from '@/features/help';
import { OnboardingPage } from '@/features/onboarding';
import { JoinPage } from '@/features/join';
import { DiscussionLibrary } from '@/features/discussion-tools/DiscussionLibrary';
import { paths as networkPaths } from '@/features/networks/contracts';
import type { ResourceKind } from '@/features/networks/contracts';

// Exported as data so the route table itself can be asserted on. The property
// that matters most here is not "the page renders" but "the page is reachable
// WITHOUT an account" — /report-illegal and the two help pages exist for people
// who have none. That is a fact about this array, not about any component, so
// router.test.tsx reads it directly. See RouteObject[] below.
export const routes = [
  // Local design review only; excluded from the production route table and bundle.
  ...(import.meta.env.DEV
    ? [
        {
          // Owner's trial of a small chapter editor (2026-09-26) — not in the composer.
          path: '/dev/chapter-editor',
          hydrateFallbackElement: <p role="status">Loading chapter editor trial…</p>,
          lazy: async () => {
            const { ChapterEditorPreview } = await import('@/features/dev/ChapterEditorPreview');
            return { Component: ChapterEditorPreview };
          },
        },
        {
          path: '/dev/rail-options',
          hydrateFallbackElement: <p role="status">Loading placement preview…</p>,
          lazy: async () => {
            const { RailOptionsPreview } = await import('@/features/dev/RailOptionsPreview');
            return { Component: RailOptionsPreview };
          },
        },
      ]
    : []),
  {
    element: <HydrationRoot />,
    children: [
      {
        path: '/',
        element: <ShellLayout />,
        children: [
          // 1.24.60 — "/" branches on session: members get the feed they have
          // always had; a signed-out visitor gets the marketing landing page.
          // A conditional rather than a redirect, so the canonical marketing
          // URL stays "/" itself. See HomeOrLanding.tsx.
          { index: true, element: <HomeOrLanding /> },
          // Public (1.24.60) — the guest feed's stable address, now that "/"
          // shows a landing page to visitors. HomePage self-handles the guest
          // case (flat public feed, no composer), so signed-out reachability
          // is a property of this row, asserted in router.test.tsx.
          { path: 'feed', element: <HomePage /> },
          { path: 'explore', element: <ExplorePage /> },
          ...['perspectives', 'perspectives/:slug'].map((path) => ({
            path,
            lazy: async () => ({
              Component: (await import('@/features/home/composer/studio/PerspectivesPage'))
                .PerspectivesPage,
            }),
          })),
          ...Object.entries(networkPaths).flatMap(([kind, path]) =>
            [path.slice(1), `${path.slice(1)}/:slug`].map((path) => ({
              path,
              lazy: async () => {
                const { NetworkPage } = await import('@/features/networks/NetworkPages');
                return { Component: () => <NetworkPage kind={kind as ResourceKind} /> };
              },
            })),
          ),
          {
            path: 'creators-hub/dashboard',
            lazy: async () => ({
              Component: (await import('@/features/networks/NetworkPages')).CreatorDashboardPage,
            }),
          },
          {
            path: 'notification-delivery',
            lazy: async () => ({
              Component: (await import('@/features/networks/NetworkPages')).DeliveryPage,
            }),
          },
          // 1.27.70: the Videos pages carry the two-factor write lock (VideoTwoFactorGate).
          {
            path: 'videos',
            element: (
              <VideoTwoFactorGate>
                <VideosPage />
              </VideoTwoFactorGate>
            ),
          },
          // Flash (formerly Shorts) lives in the shell since 1.27.50; old links redirect.
          {
            path: 'videos/flash/:id',
            element: (
              <VideoTwoFactorGate>
                <FlashPlayerPage />
              </VideoTwoFactorGate>
            ),
          },
          { path: 'videos/shorts/:id', element: <ShortsRedirect /> },
          {
            path: 'videos/:id',
            element: (
              <VideoTwoFactorGate>
                <VideoDetailPage />
              </VideoTwoFactorGate>
            ),
          },
          { path: 'live', element: <LivePage /> },
          { path: 'live/:id', element: <LiveDetailPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'messages', element: <MessagesPage /> },
          { path: 'groups', element: <MessagesPage initialFilter="group" /> },
          { path: 'bookmarks', element: <BookmarksPage /> },
          { path: 'discussions', element: <DiscussionLibrary /> },
          { path: 'premium', element: <PremiumPage /> },
          { path: 'communities', element: <CommunitiesPage /> },
          { path: 'communities/:id', element: <CommunityDetailPage /> },
          { path: 'feedback', element: <FeedbackPage /> },
          { path: 'feedback/roadmap', element: <FeedbackRoadmapPage /> },
          { path: 'feedback/:id', element: <FeedbackDetailPage /> },
          // Public (Growth slice, 1.21.60) — no auth gate, reachable signed out.
          { path: 'support', element: <SupportPage /> },
          // Public on purpose, and /report-illegal is the only report route
          // that is: the person best placed to report an intimate image of
          // themselves may have no account at all. Inside the shell rather than
          // the bare canvas so a signed-out visitor still has navigation.
          { path: 'report-illegal', element: <LegalFloorReportPage /> },
          { path: 'help/reporting', element: <HelpReportingPage /> },
          { path: 'help/safety', element: <HelpSafetyPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'profile/:handle', element: <ProfileByHandlePage /> },
          // 1.24.50 — ONE route with an optional handle segment, matching both
          // `/post/<ident>` (every link already shared, forever) and the named
          // canonical `/post/<handle>/<ident>`. Deliberately not two sibling
          // routes: the page relabels a bare arrival into the named form, and
          // two siblings would change WHICH route object matched across that
          // relabel — react-router remounts the element when the matched route
          // changes, which resets usePostHydration's `fetched` ref and refires
          // the thread request (the 1.24.00 defect that blanked the thread
          // block). One route object survives the relabel as the same instance.
          // The handle never reaches a lookup; see PostDetailPage.
          { path: 'post/:handle?/:id', element: <PostDetailPage /> },
          { path: '*', element: <NotFound /> },
        ],
      },
      {
        element: <BareLayout />,
        children: [
          { path: 'signin', element: <SignInPage /> },
          { path: 'signup', element: <SignUpPage /> },
          { path: 'verify-email', element: <VerifyEmailPage /> },
          // Public on purpose, and they must stay that way: someone locked out of
          // their account cannot sign in to reach the route that lets them back in.
          { path: 'forgot-password', element: <ForgotPasswordPage /> },
          { path: 'reset-password', element: <ResetPasswordPage /> },
          // Public on purpose (1.24.00 B2): an invite link lands on someone
          // with NO account. The page itself distinguishes signed-in visitors.
          { path: 'join/:code', element: <JoinPage /> },
          // NOT public (1.24.00 B1): the page bounces a signed-out visitor to
          // /signin?next=/onboarding itself — nothing else guards BareLayout
          // children, so do not rely on the layout for that.
          { path: 'onboarding', element: <OnboardingPage /> },
          { path: 'settings', element: <SettingsPage /> },
          // 1.27.70: the full Edit Profile page (owner ruling: under Settings). A static
          // segment, so it outranks settings/:section below.
          { path: 'settings/profile', element: <EditProfilePage /> },
          { path: 'settings/:section', element: <SettingsPage /> },
          { path: 'council', element: <CouncilPage /> },
          { path: 'council/:section', element: <CouncilPage /> },
          // Public on purpose. Someone deciding whether to join, and someone who
          // needs the reporting route without an account, must both be able to
          // read these without signing in.
          { path: 'policies', element: <PoliciesIndexPage /> },
          { path: 'policies/:docId', element: <PolicyDocumentPage /> },
          { path: 'more', element: <MorePage /> },
          { path: 'creators-hub', element: <CreatorsHubPage /> },
          { path: 'owner', element: <OwnerPage /> },
          { path: 'owner/:section', element: <OwnerPage /> },
        ],
      },
      // 1.27.60: the embeddable player — no shell at all, and the ONE route other sites may
      // put in an iframe (Caddyfile lifts X-Frame-Options for /embed/* only). Public.
      {
        path: '/embed/videos/:id',
        hydrateFallbackElement: <div style={{ position: 'fixed', inset: 0, background: '#000' }} />,
        lazy: async () => {
          const { EmbedPlayerPage } = await import('@/features/videos/embed/EmbedPlayerPage');
          return { Component: EmbedPlayerPage };
        },
      },
      // Dev scaffolding — outside the shell, full-canvas. Development builds only: these
      // pages used to be reachable on the live site by anyone who typed the address.
      ...(import.meta.env.DEV
        ? [
            { path: '/dev/components', element: <ComponentsGallery /> },
            { path: '/dev/personalization', element: <PersonalizationPanel /> },
          ]
        : []),
    ],
  },
] satisfies RouteObject[];

export const router = createBrowserRouter(routes);
